import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { isImpersonationTab } from "@pps/auth-client";
import { Sentry, toSentryError } from "@pps/observability";
import { supabase } from "@/integrations/supabase/client";
import {
  FLUSH_EVERY_TICKS,
  TICK_MS,
  addMinute,
  addPageView,
  buildKeepaliveTrackRequest,
  emptyBuffer,
  isEmptyBuffer,
  isIgnorableTrackError,
  isIgnorableTrackStatus,
  moduleFromPath,
  shouldCountMinute,
  toTrackActivityPayload,
  type ActivityBuffer,
} from "@/lib/activity-heartbeat";

/** Interações que provam que há alguém do outro lado da tela. */
const INTERACTION_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
/** Gravar o instante da interação no máximo a cada 5 s (rolagem dispara muito). */
const INTERACTION_THROTTLE_MS = 5_000;

/** Mesmas envs que o client do CRM usa (src/integrations/supabase/client.ts). */
function supabaseEnv(): { url?: string; key?: string } {
  return {
    url: import.meta.env.VITE_SUPABASE_URL as string | undefined,
    key: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined,
  };
}

/**
 * `rpc`: supabase-js, no relógio e na troca de clube/logout.
 * `keepalive`: `fetch` com keepalive, ao esconder a aba e no `pagehide` — a
 * requisição sobrevive ao fechamento da aba, o `supabase.rpc` não.
 */
type Transport = "rpc" | "keepalive";

function reportFailure(err: unknown): void {
  Sentry.captureException(toSentryError(err), {
    tags: { feature: "club-health", action: "track_activity" },
  });
}

/**
 * Heartbeat de tempo ativo da equipe no CRM (Saúde dos clubes no admin).
 *
 * - Conta 1 minuto por tique de 60 s quando a aba está visível e houve
 *   interação no último minuto; agrega por módulo (rota) e conta páginas.
 * - Envia a cada 5 min, ao esconder a aba e ao fechar/sair (`pagehide`, com
 *   `fetch` keepalive), e ao trocar de clube (o que foi medido vai para o clube
 *   anterior).
 * - Só envia com sessão: o access token fica em memória (getSession +
 *   onAuthStateChange). Sem sessão (logout) o acumulado é descartado — anon
 *   seria recusado com 42501 e viraria ruído no Sentry.
 * - Desligado sem clube ativo e numa aba de impersonação: o suporte da PPS
 *   investigando um clube não pode contar como uso do clube (o servidor também
 *   recusa pela sessão do JWT).
 * - Falha de envio é telemetria: não mostra nada ao usuário, vai ao Sentry —
 *   menos recusa de autorização (sessão acabou) e queda de rede no fechamento
 *   da aba, que não são bug.
 *
 * Montado uma vez em App.tsx (<ActivityHeartbeat />). Regra pura em
 * lib/activity-heartbeat.ts.
 */
export function useActivityHeartbeat(clubId: string | null): void {
  const { pathname } = useLocation();
  const enabled = Boolean(clubId) && !isImpersonationTab();

  const bufferRef = useRef<ActivityBuffer>(emptyBuffer());
  const clubRef = useRef<string | null>(clubId);
  const moduleRef = useRef(moduleFromPath(pathname));
  const lastInteractionRef = useRef(Date.now());
  const accessTokenRef = useRef<string | null>(null);

  const flush = useCallback(async (transport: Transport = "rpc") => {
    const club = clubRef.current;
    const buffer = bufferRef.current;
    if (!club || isEmptyBuffer(buffer)) return;
    bufferRef.current = emptyBuffer();
    const accessToken = accessTokenRef.current;
    if (!accessToken) return;
    const payload = toTrackActivityPayload(buffer, club);

    const env = supabaseEnv();
    if (transport === "keepalive" && env.url && env.key) {
      const { url, init } = buildKeepaliveTrackRequest({
        supabaseUrl: env.url,
        apiKey: env.key,
        accessToken,
        payload,
      });
      let response: Response;
      try {
        response = await fetch(url, init);
      } catch {
        // Rede caindo junto com a aba (navegador abortou no unload): não é bug.
        return;
      }
      if (!response.ok && !isIgnorableTrackStatus(response.status)) {
        reportFailure(new Error(`track_activity keepalive HTTP ${response.status}`));
      }
      return;
    }

    try {
      const { error } = await supabase.rpc("track_activity", payload);
      if (error) throw error;
    } catch (err) {
      if (isIgnorableTrackError(err)) return;
      reportFailure(err);
    }
  }, []);

  // Sessão em memória: o envio keepalive precisa do token sem esperar
  // `getSession()` (no `pagehide` não há tempo para promessa nenhuma).
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (alive) accessTokenRef.current = data.session?.access_token ?? null;
      })
      .catch(() => {
        /* sem sessão legível: o envio fica desligado até o próximo evento */
      });
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      accessTokenRef.current = session?.access_token ?? null;
    });
    return () => {
      alive = false;
      data.subscription.unsubscribe();
    };
  }, [enabled]);

  // Página aberta: conta a visita e é, por si, uma interação.
  useEffect(() => {
    moduleRef.current = moduleFromPath(pathname);
    if (!enabled) return;
    lastInteractionRef.current = Date.now();
    bufferRef.current = addPageView(bufferRef.current);
  }, [enabled, pathname]);

  useEffect(() => {
    if (!enabled) return;
    clubRef.current = clubId;
    let ticks = 0;

    const onInteraction = () => {
      const now = Date.now();
      if (now - lastInteractionRef.current >= INTERACTION_THROTTLE_MS) {
        lastInteractionRef.current = now;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") void flush("keepalive");
    };
    const onPageHide = () => {
      void flush("keepalive");
    };

    for (const evt of INTERACTION_EVENTS) {
      window.addEventListener(evt, onInteraction, { passive: true, capture: true });
    }
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);

    const timer = window.setInterval(() => {
      if (
        shouldCountMinute({
          visible: document.visibilityState === "visible",
          lastInteractionAt: lastInteractionRef.current,
          now: Date.now(),
        })
      ) {
        bufferRef.current = addMinute(bufferRef.current, moduleRef.current);
      }
      ticks += 1;
      if (ticks % FLUSH_EVERY_TICKS === 0) void flush();
    }, TICK_MS);

    return () => {
      window.clearInterval(timer);
      for (const evt of INTERACTION_EVENTS) {
        window.removeEventListener(evt, onInteraction, { capture: true });
      }
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      // Troca de clube ou impersonação: o que foi medido vai para o clube em
      // que foi medido (clubRef ainda aponta para ele). No logout o token já
      // saiu da memória (SIGNED_OUT) e o flush descarta sem enviar.
      void flush();
    };
  }, [enabled, clubId, flush]);
}
